import { useData } from "../../../../../runtime/provider";
import { forwardRef } from "react";
import { type SheetAdapterRef } from "react-native-bottom-sheet-stack";
import { continueLabel, totalMinor } from "@libs/shared-ui";
import { useAppBuyer } from "@components/Checkout/appBuyer";
import type { EngineCheckoutSession } from "@components/Checkout/engineSession";
import {
  PurchaseSheet,
  purchaseSelection,
} from "@components/Checkout/PurchaseSheet";
import { usePurchaseFunnel } from "@components/Checkout/purchaseFunnel";
import type { PurchaseFlow } from "@components/Checkout/PurchaseSheet";
import { SheetCheckout } from "@components/Checkout/SheetCheckout";
import {
  TicketSelection,
  TicketsSheet,
} from "@components/Checkout/TicketsSheet";
import { CheckoutShape } from "@components/Checkout/native/CheckoutShape";
import { useEngineCheckout } from "./EngineCheckout";

/** Select tickets (LIS-846): the purchase flow (PurchaseSheet) for an
 *  engine event's tiers, in a light sheet stacked over the access Details.
 *  Continue holds the tickets and the checkout carries on in the same
 *  sheet (LIS-975) — native on iOS and Android, the embedded web checkout
 *  on app-web: paying, then "You're going", and Done returns to the
 *  Details screen with the access refreshed. Back (or the hold running
 *  out) returns to the tickets. When the native checkout cannot finish,
 *  the hosted one takes over; when it returns the sheet closes and Details
 *  shows the refreshed access. */
export function EngineTicketsSheetContent({
  accessId,
  closeModal,
}: {
  accessId: string;
  closeModal: () => void;
}) {
  const data = useData();
  const buyer = useAppBuyer();
  // The Details' purchase funnel (LIS-1110): the hold carries its ID.
  const funnel = usePurchaseFunnel();
  const {
    tiers,
    active,
    now,
    quantities,
    lines,
    orderLimits,
    limitProblem,
    busy,
    waitlistPending,
    message,
    showMessage,
    setQuantity,
    joinWaitlist,
    hold,
    buyerDetails,
    detailsReady,
  } = useEngineCheckout(accessId, { funnelId: funnel?.id });
  // Nothing to pay for: the checkout's shape leaves the card out, and the
  // order completes on its own once the fan is attested.
  const free = totalMinor(active, lines) <= 0;

  const flow: PurchaseFlow<EngineCheckoutSession> = {
    testID: "engine-tickets-sheet",
    goods: "tickets",
    keyboard: "sheet",
    buyer,
    onFunnelStep: funnel?.step,
    choice: {
      ready: !tiers.isLoading && !tiers.isError,
      // The tiers' prices are before the booking fee the checkout adds.
      selection: purchaseSelection(
        lines.reduce((n, line) => n + line.quantity, 0),
        totalMinor(active, lines),
        active[0]?.currency ?? "",
      ),
      words: continueLabel(active, lines),
      // Below the event's minimum per order: the ticket screen says so.
      disabled:
        !lines.length || !!limitProblem || waitlistPending || !detailsReady,
      render: ({ locked }) => (
        <TicketSelection
          loading={tiers.isLoading}
          failed={tiers.isError}
          onRetry={() => void tiers.refetch()}
          locked={locked}
          tickets={{
            tiers: active,
            now,
            quantities,
            lines,
            orderLimits,
            busy: busy || waitlistPending,
            message,
            waitlistNeedsEmail: false,
            onQuantity: setQuantity,
            onWaitlist: (itemId, name, _email, request) =>
              joinWaitlist(itemId, name, request),
            buyerDetails,
          }}
        />
      ),
    },
    checkout: {
      // The tickets held in an engine session; refused, the tickets say
      // why.
      open: async () => {
        const session = await hold();
        return session
          ? { kind: "checkout", checkout: session }
          : { kind: "back" };
      },
      key: (session) => session.clientSecret,
      // Held after the sheet had gone: given back now, not when the
      // engine's 15 minutes run out.
      release: (session) => {
        void data.api.accesses.releaseEngineCheckout
          .mutate({
            accessId: session.accessId,
            clientSecret: session.clientSecret,
          })
          .catch(() => undefined);
      },
      // Continue pressed: the checkout's shape at once, while the tickets
      // are held, as on Link in Bio.
      shape: (_session, { email }) => (
        <CheckoutShape email={email} free={free} />
      ),
      render: (session, host) => (
        <SheetCheckout
          session={session}
          onBack={(note) => {
            host.back();
            showMessage(note ?? "");
            tiers.refetch().catch(() => undefined);
          }}
          onClose={host.close}
          onChrome={host.chrome}
          onSucceeded={host.succeeded}
          free={free}
        />
      ),
    },
  };
  return <PurchaseSheet flow={flow} close={closeModal} />;
}

export const EngineTicketsSheet = forwardRef<
  SheetAdapterRef,
  { accessId: string }
>(function EngineTicketsSheet({ accessId }, ref) {
  return (
    <TicketsSheet ref={ref}>
      {({ closeModal }) => (
        <EngineTicketsSheetContent
          accessId={accessId}
          closeModal={closeModal}
        />
      )}
    </TicketsSheet>
  );
});
